import axios, { type AxiosInstance } from 'axios';
import { pickBody } from '@/api/helpers';
import { attachAuthInterceptors } from '@/services/auth';

const HRMS_API = String(import.meta.env.VITE_HRMS_API_URL || 'http://localhost:5004').replace(/\/+$/, '');
const HRMS_AUDIENCE = String(import.meta.env.VITE_HRMS_AUTH_AUDIENCE || 'hrms-api');

export type HrmsHoliday = {
  id: string;
  name: string;
  date: string;
  isActive?: boolean;
  organizationId?: string;
};

export type HrmsCurrentAttendanceStatus = {
  presentToday?: boolean;
  isClockedIn?: boolean;
  isOnBreak?: boolean;
  attendanceId?: string | null;
  workedSecondsToday?: number;
};

export type HrmsDailyAttendanceRow = {
  user: {
    id: string;
    name?: string | null;
    email?: string | null;
    emp_id?: string | null;
    department?: { id: string; name: string } | null;
  };
  attendance: { id: string; status?: string | null; date?: string } | null;
};

export type HrmsDailyAttendanceReport = {
  date: string;
  rows: HrmsDailyAttendanceRow[];
};

export type HrmsAttendanceRecord = {
  id: string;
  date: string;
  status?: string | null;
};

type HrmsListHolidaysResponse =
  | { status?: string; data?: HrmsHoliday[]; meta?: { page?: number; limit?: number; total?: number; totalPages?: number; hasNext?: boolean; hasPrev?: boolean } }
  | { success?: boolean; data?: HrmsHoliday[] }
  | HrmsHoliday[];

type HrmsCurrentStatusResponse =
  | { success?: boolean; data?: HrmsCurrentAttendanceStatus }
  | HrmsCurrentAttendanceStatus;

type HrmsDailyReportResponse =
  | { success?: boolean; data?: HrmsDailyAttendanceReport }
  | HrmsDailyAttendanceReport;

type HrmsMonthlyReportResponse =
  | { success?: boolean; data?: HrmsAttendanceRecord[] }
  | HrmsAttendanceRecord[];

function createHrmsClient(): AxiosInstance {
  const client = axios.create({
    baseURL: `${HRMS_API}/api/v1`,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });

  // Token is read from cache on request; refresh is called ONLY on 401 response.
  attachAuthInterceptors(client, { audience: HRMS_AUDIENCE });

  return client;
}

const hrmsClient = createHrmsClient();

const listHolidays = async (): Promise<HrmsHoliday[]> => {
  try {
    const all: HrmsHoliday[] = [];
    let page = 1;
    const limit = 100;
    let hasNext = true;

    while (hasNext) {
      const res = await hrmsClient.get<unknown>('/masters/holidays', { params: { page, limit } });
      const raw = pickBody<HrmsListHolidaysResponse>(res.data);
      if (Array.isArray(raw)) { all.push(...raw); break; }
      all.push(...(raw?.data ?? []));
      hasNext = 'meta' in raw ? Boolean(raw.meta?.hasNext) : false;
      page += 1;
      if (page > 100) break;
    }

    return all;
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      return [];
    }
    throw error;
  }
};

const getCurrentAttendanceStatus = async (): Promise<HrmsCurrentAttendanceStatus> => {
  try {
    const res = await hrmsClient.get<unknown>('/attendance/current-status');
    const raw = pickBody<HrmsCurrentStatusResponse>(res.data);
    if (raw && typeof raw === 'object' && 'data' in raw) {
      return (raw as { data?: HrmsCurrentAttendanceStatus }).data ?? {};
    }
    return (raw as HrmsCurrentAttendanceStatus) ?? {};
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      return {};
    }
    throw error;
  }
};

const getDailyAttendanceReport = (date: string): Promise<HrmsDailyAttendanceReport> =>
  hrmsClient.get<unknown>('/attendance/admin/report/daily', { params: { date } }).then((res) => {
    const raw = pickBody<HrmsDailyReportResponse>(res.data);
    if (raw && typeof raw === 'object' && 'data' in raw) {
      return (raw as { data?: HrmsDailyAttendanceReport }).data ?? { date, rows: [] };
    }
    return (raw as HrmsDailyAttendanceReport) ?? { date, rows: [] };
  });

const getMonthlyAttendanceReport = async (month: number, year: number): Promise<HrmsAttendanceRecord[]> => {
  try {
    const res = await hrmsClient.get<unknown>('/attendance/report', { params: { month, year } });
    const raw = pickBody<HrmsMonthlyReportResponse>(res.data);
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      return [];
    }
    throw error;
  }
};

export const hrmsApi = {
  listHolidays,
  getCurrentAttendanceStatus,
  getDailyAttendanceReport,
  getMonthlyAttendanceReport,
};
